/**
 * React Cache Wrapper
 * Intercepts React rendering to use cached content when available
 */

import { tabStateCache } from './tabStateCache';

/**
 * Enhanced dangerouslySetInnerHTML that checks cache before rendering
 * This should be used instead of regular dangerouslySetInnerHTML in components
 */
export const createCachedInnerHTML = (originalContent, leadId = null, docType = null) => {
    // If we have cache key components, check for cached content first
    if (leadId && docType) {
        // Create multiple possible cache key variations
        const possibleKeys = [];
        
        // Special mappings for known tab names
        const specialMappings = {
            'questions for pd': 'pd_questions',
            'questions_for_pd': 'pd_questions',
            'map insights': 'map_insights',
            'map_insights': 'map_insights',
            'industry risk insights': 'industry_insights',
            'industry_risk_insights': 'industry_insights',
            '[new] industry risk insights': 'industry_insights',
            'new industry risk insights': 'industry_insights'
        };
        
        // Check for special mappings first
        const lowerDocType = docType.toLowerCase().trim();
        const cleanedLowerDocType = lowerDocType.replace(/\[.*?\]/g, '').trim();
        
        if (specialMappings[lowerDocType]) {
            possibleKeys.push(tabStateCache.generateKey(leadId, specialMappings[lowerDocType]));
        }
        if (specialMappings[cleanedLowerDocType] && cleanedLowerDocType !== lowerDocType) {
            possibleKeys.push(tabStateCache.generateKey(leadId, specialMappings[cleanedLowerDocType]));
        }
        
        // 1. Basic normalization: "Map Insights" -> "map_insights"
        const normalizedDocType = docType.toLowerCase().replace(/\s+/g, '_');
        possibleKeys.push(tabStateCache.generateKey(leadId, normalizedDocType));
        
        // 2. Remove brackets and extra words: "[New] Industry Risk Insights" -> "industry_insights"
        const cleanedDocType = docType
            .replace(/\[.*?\]/g, '') // Remove [New], [Updated], etc.
            .replace(/\b(risk|new|updated|latest)\b/gi, '') // Remove common extra words
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_');
        if (cleanedDocType !== normalizedDocType) {
            possibleKeys.push(tabStateCache.generateKey(leadId, cleanedDocType));
        }
        
        // 3. Try just the main keywords: "Industry Risk Insights" -> "industry_insights"
        const keywordDocType = docType
            .replace(/\[.*?\]/g, '')
            .replace(/\b(risk|new|updated|latest)\b/gi, '')
            .split(/\s+/)
            .filter(word => word.length > 2) // Keep meaningful words
            .join('_')
            .toLowerCase();
        if (keywordDocType !== cleanedDocType && keywordDocType !== normalizedDocType) {
            possibleKeys.push(tabStateCache.generateKey(leadId, keywordDocType));
        }
        
        // Remove duplicates while preserving order
        const uniqueKeys = [...new Set(possibleKeys)];
        
        // Try each possible key
        for (const cacheKey of uniqueKeys) {
            const cachedContent = tabStateCache.get(cacheKey);
            if (cachedContent) {
                return { __html: cachedContent };
            }
        }
    }
    
    // Fallback to original content
    return { __html: originalContent };
};

/**
 * Hook to get cached content or original content
 * Use this in React components to get the right content to render
 */
export const useCachedContent = (originalContent, leadId = null, docType = null) => {
    if (leadId && docType) {
        const cacheKey = tabStateCache.generateKey(leadId, docType);
        const cachedContent = tabStateCache.get(cacheKey);
        
        if (cachedContent) {
            return cachedContent;
        }
    }
    
    return originalContent;
};
